import classNames from 'classnames';
import React, { useMemo } from 'react';

import { TheaterReservationTracking } from 'common/constants/trackingEventsNames';
import getVersionLabel, {
  ShowtimeGroupKey
} from 'common/tools/showtime/getVersionLabel';
import trans from 'common/tools/translations/trans';

import { PartialShowtime } from 'website/containers/showtimes/utils/types';

import ShowtimesVersion, { OpenModalShowtimes } from './ShowtimesVersion';

type ShowtimesVersionHolderProps = {
  additionalClassName?: string | null;
  hideDate?: boolean;
  openModal?: OpenModalShowtimes;
  showtimes: Partial<Record<ShowtimeGroupKey, PartialShowtime[]>>;
  theaterHasBooking?: boolean | null;
  tracking: Omit<TheaterReservationTracking, 'showtime'>;
};

const VERSION_ORDER: ShowtimeGroupKey[] = [
  'dubbed',
  'local',
  'dubbed_st',
  'local_st',
  'dubbed_st_sme',
  'local_st_sme'
];

const ShowtimesVersionHolder = ({
  additionalClassName,
  hideDate,
  openModal,
  showtimes,
  theaterHasBooking,
  tracking
}: ShowtimesVersionHolderProps) => {
  const sortedShowtimes = useMemo(() => {
    return Object.entries(showtimes).sort(([keyA], [keyB]) => {
      const indexA = VERSION_ORDER.indexOf(keyA as ShowtimeGroupKey);
      const indexB = VERSION_ORDER.indexOf(keyB as ShowtimeGroupKey);

      if (indexA !== -1 && indexB === -1) return -1;
      if (indexA === -1 && indexB !== -1) return 1;
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;

      return keyA.localeCompare(keyB);
    });
  }, [showtimes]);

  const showtimesVersions = (
    <>
      {sortedShowtimes.map(([versionKey, hours]) => {
        if (!hours || hours.length === 0) {
          return null;
        }

        const label = getVersionLabel(versionKey as ShowtimeGroupKey);

        return (
          <ShowtimesVersion
            hideDate={hideDate}
            key={versionKey}
            openModal={openModal}
            showtimesHours={hours}
            showtimesLabel={label}
            tracking={tracking}
          />
        );
      })}

      {theaterHasBooking && (
        <p className="e-ticket icon icon-ticket">
          <span className="e-ticket-text">
            {trans('moviepage_showtimes.e-ticket.text')}
          </span>
        </p>
      )}
    </>
  );

  const finalClassName = classNames(
    'showtimes-versions-holder',
    additionalClassName
  );

  return <div className={finalClassName}>{showtimesVersions}</div>;
};

export default ShowtimesVersionHolder;
